import { formatDateTime, formatINR } from "@templeos/shared";
import { Alert, Button, Drawer, Money, StatCard } from "@templeos/ui";
import { useNavigate } from "react-router";
import { db } from "../lib/db.js";
import { shiftSales, shiftTickets, tally } from "../lib/queue.js";
import { cashLevel } from "../lib/rules.js";
import type { QueuedSale } from "../lib/types.js";
import { useSession } from "../session.js";
import { useLive } from "./hooks.js";

const NONE: QueuedSale[] = [];

/**
 * This shift's bills on this terminal (offline ones included), live from IndexedDB, and the tickets that count against
 * the cashier's shift quota (the server's figure plus what it has not seen yet).
 */
export function useShiftTally() {
  const { shift, config } = useSession();
  const shiftId = shift?.shift.id ?? null;
  const sales = useLive(() => (shiftId ? shiftSales(db, shiftId) : Promise.resolve(NONE)), shiftId ?? "none", NONE);
  const t = tally(sales);
  const drawer = (shift?.shift.opening_float ?? 0) + t.cash;
  return { sales, t, drawer, quotaUsed: shiftTickets(config?.cashierQuota ?? null, shiftId, sales) };
}

/** Counter cash limit alert (SOW Module 2). */
export function CashAlert({ drawer }: { drawer: number }) {
  const { config } = useSession();
  const level = cashLevel(drawer, config?.cashLimit ?? null);
  if (level === "ok" || !config?.cashLimit) return null;
  return (
    <Alert tone={level === "over" ? "danger" : "warning"}>
      Drawer cash {formatINR(drawer)} {level === "over" ? "is above" : "is close to"} this counter's limit of {formatINR(config.cashLimit)}.
      Ask the supervisor for a cash pickup{level === "over" ? " now" : ""}.
    </Alert>
  );
}

/** Cashier shift summary: this terminal's live tally next to the server's figures for the shift. */
export function ShiftSummaryView() {
  const { shift, reachable } = useSession();
  const { t, drawer } = useShiftTally();
  if (!shift) return <Alert tone="info">No shift is open.</Alert>;
  return (
    <div className="tos-stack">
      <div className="tos-muted">Opened {formatDateTime(shift.shift.opened_at)} at {shift.shift.counter_code} · float {formatINR(shift.shift.opening_float)}</div>
      <div className="tos-grid">
        <StatCard label="Bills" value={t.bills} hint={t.rejected ? `${t.rejected} refused by server` : "this terminal"} />
        <StatCard label="Tickets issued" value={t.tickets} tone="accent" />
        <StatCard label="Cash collected" value={<Money paise={t.cash} />} />
        <StatCard label="UPI collected" value={<Money paise={t.upi} />} tone="info" />
        <StatCard label="Drawer should hold" value={<Money paise={drawer} />} hint="opening float + cash" tone="success" />
        <StatCard label="Waiting to sync" value={t.pending} hint={t.pending ? formatINR(t.pendingAmount) : "all on the server"} tone={t.pending ? "danger" : undefined} />
      </div>
      <h3 style={{ margin: "8px 0 0" }}>Server figures{reachable ? "" : " (last known)"}</h3>
      <div className="tos-grid">
        <StatCard label="Receipts issued" value={shift.counts.issued} hint={`${shift.counts.cancelled} cancelled`} />
        <StatCard label="Expected cash" value={<Money paise={shift.expectedCash} />} />
        {shift.byMode.map((m) => <StatCard key={m.mode} label={`${m.mode.toUpperCase()} (${m.receipts})`} value={<Money paise={m.amount} />} />)}
      </div>
    </div>
  );
}

export function ShiftDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { refreshShift } = useSession();
  return (
    <Drawer open={open} title="My shift" onClose={onClose}
      footer={<><Button onClick={() => void refreshShift()}>Refresh</Button><Button variant="primary" onClick={() => navigate("/shift")}>Count cash & close shift</Button></>}>
      <ShiftSummaryView />
    </Drawer>
  );
}
